// Blip's models (docs/BLIP_MODELS_DESIGN.md): his tools make_model,
// change_model and make_picture, carried out in the page, and make_sound and
// make_music (docs/BLIP_SOUNDS_DESIGN.md). Each is a job on the IDE's server
// (web/server/lib/models.mjs), which has Tripo make a model, OpenAI a
// picture or a texture, and ElevenLabs a sound effect or music, which the
// server makes ready for a game as a WAV or Ogg Vorbis file. The tool shows the job on a
// card in Blip's panel, waits for it, saves what it made in the project, and
// tells Claude about it, with a picture of it. A card whose tool stopped
// waiting, or whose page closed, carries on: its job is followed again when
// its project is open, and saved then. A texture is saved as a set: its
// colour map, and its normal, height and roughness maps beside it. A sound's
// variations are saved side by side, numbered.
//
// A card is an item of the chat's log: { kind: "model", job, jobKind,
// title, status (asking, declined, running, done, saved or failed), step,
// waiting, progress, share, note, error, project, want ({ path, width,
// height }), texture (a texture's set), from (the model a change started
// from), path (where it was saved), maps (where a texture's other maps were
// saved, by kind), paths (a sound's files, its variations in order), sounds
// (what the server found of each: seconds, channels, loop, join, wave and
// more), thumb (a small picture of it, as a data URL), source (a small
// picture of the one it is made from), kept (where the user's picture it was
// made from was kept), summary, and for the
// shared library: shareable, asksSharing (the card asks), shared (the member
// chose to share it), rebate (the share of a day's energy that comes back) and
// shareState (the server's { status, asset, reason, rebate }) }.

import { hashBlob } from "../cloud.js";
import { normalisePath, splitPath } from "../projects.js";
import { describeGlb, isGlb, joinClips, nameClips, noteOrigin, readGlb, shrinkTextures, standGlb, writeGlb } from "./glb.js";
import { CROP_SIDE, GuideError, cropPicture, pictureNumber } from "./guides.js";
import { MAP_SUFFIXES, mapPath } from "./texture_names.js";

export class ModelToolError extends Error {}

// A job that uses this much of a day's energy, or more than half of what is
// left, waits for the user's click.
export const CONFIRM_SHARE = 0.3;
const POLL_MS = 2000;
// A shared job's library steps, after the job: followed this often, for up
// to ten minutes.
const SHARE_PENDING = ["waiting", "making", "checking"];
const SHARE_POLL_MS = 5000;
const THUMB_WIDTH = 240;
const GENERATOR = "Tripo AI, made for Blitz3D+ by Blip";

const STEPS = { upload: "Sending it to Tripo", generate: "Making it", check: "Checking it can have a skeleton",
  rig: "Giving it a skeleton", retarget: "Teaching it to move", texture: "Painting it", reduce: "Simplifying it",
  picture: "Drawing it", height: "Making its height map", roughness: "Making its roughness map", maps: "Finishing its maps",
  sound: "Making the sound", compose: "Composing it", loop: "Making it loop", finish: "Finishing it" };

// The jobs ElevenLabs makes.
const AUDIO_JOBS = new Set(["sound", "music"]);

// Who makes each kind of job, for the card when it fails.
export const makerOf = (jobKind) => jobKind === "picture" ? "OpenAI" : AUDIO_JOBS.has(jobKind) ? "ElevenLabs" : "Tripo";

const SKELETONS = { biped: "a person's", quadruped: "a four-legged creature's", hexapod: "a six-legged creature's",
  octopod: "an eight-legged creature's", avian: "a bird's", serpentine: "a snake's", aquatic: "a fish's" };

const plural = (count, one, many = `${one}s`) => `${count.toLocaleString("en")} ${count === 1 ? one : many}`;
const percent = (share) => `${Math.max(share > 0 ? 1 : 0, Math.round(share * 100))}%`;
const size = (bytes) => bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1048576).toFixed(1)} MB`;
const number = (value) => String(+value.toFixed(2));
const sleep = (ms, signal) => new Promise((resolve) => {
  const timer = setTimeout(resolve, ms);
  if (signal) signal.addEventListener("abort", () => { clearTimeout(timer); resolve(); }, { once: true });
});

// A name for a file: "Goblin King!" is goblin-king.
export function slug(name) {
  return String(name || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "").slice(0, 40) || "model";
}

// A path no file of the project has: barrel.glb, then barrel-2.glb.
export function freePath(project, wanted) {
  const taken = new Set(project.paths().map((path) => path.toLowerCase()));
  if (!taken.has(wanted.toLowerCase())) return wanted;
  const dot = wanted.lastIndexOf(".");
  const stem = wanted.slice(0, dot), ext = wanted.slice(dot);
  for (let n = 2; ; ++n) if (!taken.has(`${stem}-${n}${ext}`.toLowerCase())) return `${stem}-${n}${ext}`;
}

// A picture's bytes as an image the canvas can draw.
// Its pixels as they are: a model's normal and roughness maps are data, not
// colours.
async function bitmap(bytes) {
  return createImageBitmap(new Blob([bytes]), { colorSpaceConversion: "none", premultiplyAlpha: "none" });
}

// A picture drawn at a size, halving first, which keeps a big picture sharp
// when it is made small. Returns an OffscreenCanvas.
async function scaled(bytes, width, height) {
  let source = await bitmap(bytes);
  let current = { width: source.width, height: source.height };
  while (current.width / 2 >= width && current.height / 2 >= height) {
    const half = new OffscreenCanvas(Math.round(current.width / 2), Math.round(current.height / 2));
    const context = half.getContext("2d");
    context.imageSmoothingQuality = "high";
    context.drawImage(source, 0, 0, half.width, half.height);
    if (source.close) source.close();
    source = half;
    current = { width: half.width, height: half.height };
  }
  const canvas = new OffscreenCanvas(width, height);
  const context = canvas.getContext("2d");
  context.imageSmoothingQuality = "high";
  context.drawImage(source, 0, 0, width, height);
  if (source.close) source.close();
  return canvas;
}

const dataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

export class ModelTools {
  // ide: { cloud, project(), projectPath(path), addFile(path, bytes) (saves a
  // file in the open project and shows it), worldEditor(), putToolFile(path,
  // bytes) (gives the open World Editor a file) }.
  // The panel sets panel: { add(item), update(item), confirm(item, signal)
  // (whether the user said yes), progress(text), energyChanged(),
  // choices(list), chat() (the chat's id), picture(n) (the user's picture n
  // in the chat, guides.js chatPicture) }.
  constructor(ide) {
    this.ide = ide;
    this.panel = null;
    this.following = new Map();     // job id -> the promise of its end
    this.previews = new Map();      // job id -> Tripo's picture of it, for Claude
    this.made = 0;                  // saved since BlipTools.begin
    // Pictures are kept one at a time, so that two never take one free path.
    this.keeping = Promise.resolve();
  }

  // Whether the server makes models (Tripo), pictures (OpenAI), or sound
  // effects or music (ElevenLabs): kind is "model", "picture", "sound" or
  // "music", or any when not given.
  available(kind = null) {
    const { cloud } = this.ide;
    const models = cloud && cloud.config && cloud.config.blip && cloud.config.blip.models;
    if (!models) return false;
    if (!kind || !models.makes) return true;
    return !!models.makes[{ picture: "pictures", sound: "sounds", music: "music" }[kind] || "models"];
  }

  describe(call) {
    const input = call.input || {};
    switch (call.name) {
      case "make_model": return `Making ${input.name || "a model"}`;
      case "change_model": return `${{ retexture: "Painting", reduce: "Simplifying", animate: "Animating" }[input.change] || "Changing"} ` +
        `${input.path || "a model"}`;
      case "make_picture": return `${input.texture ? "Making the texture" : "Drawing"} ${input.name || "a picture"}`;
      case "make_sound": return `Making the sound ${input.name || ""}`.trim();
      case "make_music": return `Composing ${input.name || "music"}`;
      case "offer_choices": return "Offering some answers";
      case "find_assets": return `Looking in the shared library for "${input.query || ""}"`;
      case "add_asset": return "Adding a shared asset";
      case "keep_picture": return `Keeping ${pictureNumber(input.picture) ? `picture ${pictureNumber(input.picture)}` : "a picture"}` +
        `${input.crop ? ", a part of it" : ""}`;
      default: return "Working";
    }
  }

  async run(name, input, signal) {
    if (name === "offer_choices") return this.offerChoices(input);
    if (name === "keep_picture") {
      if (!this.ide.project()) throw new ModelToolError("No project is open.");
      return this.keepPicture(input);
    }
    if (name === "find_assets" || name === "add_asset") {
      if (!this.libraryAvailable()) throw new ModelToolError("The shared asset library is not available here.");
      if (!this.ide.cloud.user) throw new ModelToolError("The user is not signed in, and the shared library needs an account.");
      if (name === "find_assets") return this.findAssets(input);
      if (!this.ide.project()) throw new ModelToolError("No project is open.");
      return this.addAsset(input);
    }
    if (name === "make_picture" && !this.available("picture")) {
      throw new ModelToolError("Making pictures is not available here: the IDE's server has no OpenAI key. Tell the user, " +
        "and suggest a picture from the help's samples or one they have.");
    }
    if ((name === "make_sound" || name === "make_music") && !this.available(name === "make_sound" ? "sound" : "music")) {
      throw new ModelToolError(`Making ${name === "make_sound" ? "sound effects" : "music"} is not available here. Tell the user, ` +
        "and suggest a sound from the help's samples (samples/audio/media, samples/fps/assets/sounds) or one they have.");
    }
    if (!["make_picture", "make_sound", "make_music"].includes(name) && !this.available("model")) {
      throw new ModelToolError("Making models is not available here: the IDE's server has no Tripo key. " +
        "Tell the user, and suggest a model from the help's samples or one they have.");
    }
    if (!this.ide.cloud.user) throw new ModelToolError("The user is not signed in, and making models needs an account.");
    if (!this.ide.project()) throw new ModelToolError("No project is open.");
    switch (name) {
      case "make_model": return this.makeModel(input, signal);
      case "change_model": return this.changeModel(input, signal);
      case "make_picture": return this.makePicture(input, signal);
      case "make_sound": return this.makeSound(input, signal);
      case "make_music": return this.makeMusic(input, signal);
      default: throw new ModelToolError(`There is no tool called ${name}.`);
    }
  }

  offerChoices(input) {
    const choices = Array.isArray(input.choices) ? input.choices.filter((choice) => typeof choice === "string" && choice.trim())
      .map((choice) => choice.trim().slice(0, 60)).slice(0, 6) : [];
    if (choices.length < 2) throw new ModelToolError("Give 2 to 6 short answers as choices.");
    if (this.panel) this.panel.choices(choices);
    return "The choices are shown under your message. End your turn now: the user's answer comes as their next message.";
  }

  // ------------------------------------------------------------- inputs

  // A path the tools were given, as the project spells it, or null.
  projectFile(path) {
    if (typeof path !== "string" || !path.trim()) return null;
    try {
      return this.ide.projectPath(normalisePath(path.trim().replace(/^(\.\/)+/, "")));
    } catch {
      return null;
    }
  }

  // Where a result is saved: a path given, made right, or name's in folder.
  wantedPath(path, name, folder, ext) {
    if (typeof path === "string" && path.trim()) {
      let text = path.trim().replace(/^(\.\/)+/, "").replace(/^\/+/, "");
      if (/\\|^[a-z]+:/i.test(text)) throw new ModelToolError(`${path} is not a path in the project: use forward slashes, as in ${folder}/${slug(name)}${ext}.`);
      if (splitPath(text).some((part) => part === "..")) throw new ModelToolError(`${path} is not a path in the project.`);
      text = normalisePath(text);
      if (!text.toLowerCase().endsWith(ext)) text = `${text.replace(/\.[a-z0-9]{1,5}$/i, "")}${ext}`;
      return text;
    }
    return `${folder}/${slug(name)}${ext}`;
  }

  // Sends a picture to the server, as a project's files are sent, as a PNG
  // or JPEG: a picture of the project, by its path, or one the user showed in
  // the chat, by its number. keep: where in the project a shown picture is
  // saved first (without its extension), or null to send it without keeping
  // it. sources, when given, gets { thumb, kept } for the card.
  // Returns { hash, type }.
  async upload(value, keep = null, sources = null) {
    const file = await this.pictureFile(value, keep);
    await this.send(file);
    if (sources) sources.push({ thumb: await this.thumbnail(file.bytes, file.type), kept: file.kept || null });
    return { hash: file.hash, type: file.type };
  }

  // A picture the tools were given, as it is sent: { blob, hash, type,
  // bytes, path, kept }. A number is one the user showed (pictureNumber),
  // unless the project has a file of that name.
  async pictureFile(value, keep = null) {
    const n = pictureNumber(value);
    if (n === null || this.projectFile(String(value))) return this.prepare(value, "picture");
    const shown = this.shown(n);
    let kept = null;
    if (keep) ({ path: kept } = await this.keep(`${keep}${shown.type === "image/png" ? ".png" : ".jpg"}`, shown.bytes));
    const blob = new Blob([shown.bytes], { type: shown.type });
    return { blob, hash: await hashBlob(blob), type: shown.type, bytes: shown.bytes, path: kept, kept };
  }

  // The user's picture n in the chat. Throws when there is none.
  shown(n) {
    const picture = this.panel && this.panel.picture ? this.panel.picture(n) : null;
    if (!picture) {
      const count = this.panel && this.panel.pictureCount ? this.panel.pictureCount() : 0;
      throw new ModelToolError(`There is no picture ${n} in this chat: the user has shown ${count ?
        `${count === 1 ? "one picture" : `${count} pictures`}, numbered from 1` : "no pictures"}.`);
    }
    return picture;
  }

  // Saves a picture in the project at a path no file has (freePath), and
  // gives it to the open World Editor. Returns { path }.
  keep(wanted, bytes) {
    const done = this.keeping.then(async () => {
      const project = this.ide.project();
      const path = freePath(project, wanted);
      await this.ide.addFile(path, bytes);
      if (this.ide.worldEditor()) await this.ide.putToolFile(path, bytes);
      return { path };
    });
    this.keeping = done.catch(() => {});
    return done;
  }

  // keep_picture: one of the user's pictures in this chat, or a part of it,
  // saved in the project, as it was prepared to be sent. A part comes back
  // too, for Claude to check.
  async keepPicture(input) {
    const n = pictureNumber(input.picture);
    if (n === null) throw new ModelToolError("picture is the number of a picture the user showed you, such as 2.");
    let picture = this.shown(n);
    if (input.crop !== undefined) {
      try {
        picture = { ...picture, ...(await cropPicture(picture, input.crop)) };
      } catch (error) {
        throw error instanceof GuideError ? new ModelToolError(error.message) : error;
      }
    }
    const ext = picture.type === "image/png" ? ".png" : ".jpg";
    const stem = slug(picture.name.replace(/\.[a-z0-9]{1,5}$/i, ""));
    const name = ["image", "picture", "blob", "model"].includes(stem) ? `picture-${n}` : stem;
    const wanted = this.wantedPath(input.path, name, "pictures", ext);
    const { path } = await this.keep(wanted, picture.bytes);
    const lines = [`Kept picture ${n}${input.crop ? ", the part asked for," : ""} as ${path} (${picture.width} x ${picture.height} ` +
      `pixels, ${size(picture.bytes.byteLength)}). The Files pane shows it.`];
    if (picture.small) {
      lines.push(`It is small, under ${CROP_SIDE} pixels on its long side, which is too little to make a model from straight ` +
        "away: make a larger, clean picture of it first (make_picture, with this path as picture, saying what it is), and " +
        "the model from that.");
    }
    if (this.ide.worldEditor()) lines.push("The World Editor has it too: import it as an asset with world_action import.");
    const content = [{ type: "text", text: lines.join(" ") }];
    if (input.crop) {
      content[0].text += " This is the part kept:";
      content.push({ type: "image", source: { type: "base64", media_type: picture.type, data: base64(picture.bytes) } });
    }
    return content;
  }

  // A file the server is sent when it asks: { blob, hash, type }.
  async send({ blob, hash }) {
    const { cloud } = this.ide;
    const { missing } = await cloud.request("POST", "blobs/missing", { json: { hashes: [hash] } });
    if (missing.includes(hash)) await cloud.request("PUT", `blobs/${hash}`, { body: blob });
  }

  // A project file as it is sent: a picture as PNG or JPEG, a model as GLB.
  // Returns { blob, hash, type, bytes }.
  async prepare(path, kind) {
    const project = this.ide.project();
    const actual = this.projectFile(path);
    if (!actual) throw new ModelToolError(`There is no file ${path} in the project. View the project's files to see their names.`);
    let bytes = new Uint8Array(await project.blob(actual).arrayBuffer());
    let type;
    if (kind === "model") {
      if (!isGlb(bytes)) throw new ModelToolError(`${actual} is not a .glb model: only GLB models can be changed.`);
      type = "model/gltf-binary";
    } else if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes.byteLength < 20e6) {
      type = "image/png";
    } else if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes.byteLength < 20e6) {
      type = "image/jpeg";
    } else {
      // Another kind of picture, or a very large one, goes as a PNG.
      let image;
      try {
        image = await bitmap(bytes);
      } catch {
        throw new ModelToolError(`${actual} is not a picture: use a PNG, JPEG or WebP picture.`);
      }
      const scale = Math.min(1, 2048 / Math.max(image.width, image.height));
      image.close();
      const canvas = await scaled(bytes, Math.max(1, Math.round(image.width * scale)), Math.max(1, Math.round(image.height * scale)));
      bytes = new Uint8Array(await (await canvas.convertToBlob({ type: "image/png" })).arrayBuffer());
      type = "image/png";
    }
    const blob = new Blob([bytes], { type });
    return { blob, hash: await hashBlob(blob), type, bytes, path: actual };
  }

  // ---------------------------------------------------------------- tools

  async makeModel(input, signal) {
    if (typeof input.name !== "string" || !input.name.trim()) throw new ModelToolError("Give the model a short name, such as Barrel.");
    const description = typeof input.description === "string" ? input.description.trim() : "";
    if (input.picture && input.views) throw new ModelToolError("Give a picture or views, not both.");
    if (!description && !input.picture && !input.views) throw new ModelToolError("Give a description, a picture or views to make it from.");
    const name = input.name.trim().slice(0, 40);
    const job = { kind: "model", title: name, style: input.style, triangles: input.triangles, texture: input.texture,
      realSize: input.real_size, parts: input.parts, animations: input.animations, rig: input.skeleton };
    // With a picture or views, the description is not used. A picture the
    // user showed is kept beside the model, so the project says where the
    // model came from: models/dragon-picture.jpg, models/dragon-front.png.
    const wanted = this.wantedPath(input.path, name, "models", ".glb");
    const stem = wanted.replace(/\.glb$/i, "");
    const sources = [];
    if (input.picture) {
      job.picture = await this.upload(input.picture, `${stem}-picture`, sources);
    } else if (input.views) {
      if (typeof input.views !== "object") {
        throw new ModelToolError("views is { front, left, back, right }, each a picture's path or the number of one the user showed.");
      }
      job.views = {};
      for (const side of ["front", "left", "back", "right"]) {
        if (input.views[side]) job.views[side] = await this.upload(input.views[side], `${stem}-${side}`, sources);
      }
    } else {
      job.prompt = description;
      if (typeof input.avoid === "string" && input.avoid.trim()) job.avoid = input.avoid.trim();
    }
    const item = { kind: "model", jobKind: "model", title: name, textureSize: input.texture === "hd" ? 2048 : 1024,
      want: { path: wanted }, ...sourcesOf(sources) };
    return this.result(await this.start(job, item, signal), signal);
  }

  async changeModel(input, signal) {
    const path = this.projectFile(input.path);
    if (!path) throw new ModelToolError(`There is no file ${input.path} in the project.`);
    const change = input.change;
    if (!["retexture", "reduce", "animate"].includes(change)) throw new ModelToolError("change is retexture, reduce or animate.");
    // A model Blip had made names its Tripo jobs, which a change starts from:
    // it is sent only when the server asks, and a new skeleton is not needed
    // for more animations.
    const source = await this.prepare(path, "model");
    let origin = null;
    try {
      origin = describeGlb(readGlb(source.bytes)).origin;
    } catch (error) {
      throw new ModelToolError(`${path} could not be read: ${error.message}`);
    }
    const name = path.replace(/^.*\//, "").replace(/\.glb$/i, "");
    const job = { kind: "change", change, title: name[0].toUpperCase() + name.slice(1),
      source: { hash: source.hash, type: source.type, ...(origin ? { origin } : {}) } };
    const sources = [];
    if (change === "retexture") {
      if (typeof input.description === "string" && input.description.trim()) job.prompt = input.description.trim();
      else if (input.picture) job.picture = await this.upload(input.picture, null, sources);
      else throw new ModelToolError("Say what the new texture looks like (description), or give a picture.");
      if (input.texture) job.texture = input.texture;
    } else if (change === "reduce") {
      job.triangles = input.triangles;
    } else {
      job.animations = input.animations;
    }
    const suffix = { retexture: "retextured", reduce: "simpler", animate: "animated" }[change];
    const stem = path.replace(/\.glb$/i, "");
    const item = { kind: "model", jobKind: "change", change, textureSize: input.texture === "hd" ? 2048 : 1024, title: `${job.title}: ${{ retexture: "new texture", reduce: "fewer triangles",
      animate: "animations" }[change]}`, from: path, origin, ...sourcesOf(sources),
      want: { path: input.save_as ? this.wantedPath(input.save_as, stem, "models", ".glb") : `${stem}-${suffix}.glb` } };
    return this.result(await this.start(job, item, signal, [source]), signal);
  }

  async makePicture(input, signal) {
    if (typeof input.name !== "string" || !input.name.trim()) throw new ModelToolError("Give the picture a short name, such as Ship.");
    if (typeof input.description !== "string" || !input.description.trim()) throw new ModelToolError("Say what the picture shows.");
    const texture = !!input.texture;
    if (texture && input.transparent) throw new ModelToolError("A texture covers a surface, so it has no transparent background.");
    const name = input.name.trim().slice(0, 40);
    // The user's effort setting chooses how well it is drawn.
    const effort = this.panel && this.panel.level ? this.panel.level() : null;
    const job = { kind: "picture", title: name, prompt: input.description.trim(), transparent: !!input.transparent,
      shape: texture ? "square" : input.shape, ...(texture ? { texture: true, metal: !!input.metal } : {}),
      ...(effort ? { effort } : {}) };
    const sources = [];
    if (input.picture) job.picture = await this.upload(input.picture, null, sources);
    const whole = (value) => Number.isInteger(value) && value >= 8 && value <= 2048 ? value : null;
    // A texture stays square: one side given sets both.
    const side = texture ? whole(input.width) || whole(input.height) : null;
    const item = { kind: "model", jobKind: "picture", title: name, transparent: !!input.transparent, texture,
      metal: texture && !!input.metal, ...sourcesOf(sources),
      want: { path: this.wantedPath(input.path, name, texture ? "textures" : "pictures", ".png"),
        width: texture ? side : whole(input.width), height: texture ? side : whole(input.height) } };
    return this.result(await this.start(job, item, signal), signal);
  }

  // make_sound: a sound effect, or a few variations of it, seconds long; a
  // loop for anything that goes on. Saved in sounds/, as a WAV or an Ogg
  // Vorbis file, whichever the server made.
  async makeSound(input, signal) {
    if (typeof input.name !== "string" || !input.name.trim()) throw new ModelToolError("Give the sound a short name, such as Door creak.");
    if (typeof input.description !== "string" || !input.description.trim()) throw new ModelToolError("Say what the sound is.");
    if (typeof input.seconds !== "number" || !(input.seconds >= 0.5 && input.seconds <= 30)) {
      throw new ModelToolError("Give its length in seconds, from 0.5 to 30, such as 0.5 for a footstep, 2 for a door, or 10 to 20 " +
        "for a loop.");
    }
    const name = input.name.trim().slice(0, 40);
    const job = { kind: "sound", title: name, prompt: input.description.trim(), seconds: input.seconds, loop: !!input.loop,
      spatial: input.spatial !== false, variations: input.variations ?? 1, level: input.level };
    const item = { kind: "model", jobKind: "sound", title: name, loop: !!input.loop, spatial: input.spatial !== false,
      want: { path: this.wantedPath(input.path, name, "sounds", ".wav").replace(/\.wav$/i, "") } };
    return this.result(await this.start(job, item, signal), signal);
  }

  // make_music: music for a game, a loop at a tempo unless it is a theme with
  // an ending; instrumental unless it is a song. Saved in music/, as Ogg
  // Vorbis.
  async makeMusic(input, signal) {
    if (typeof input.name !== "string" || !input.name.trim()) throw new ModelToolError("Give the music a short name, such as Forest theme.");
    if (typeof input.description !== "string" || !input.description.trim()) {
      throw new ModelToolError("Describe the music: its genre, mood, instruments and tempo.");
    }
    const name = input.name.trim().slice(0, 40);
    const loop = input.loop !== false;
    const job = { kind: "music", title: name, prompt: input.description.trim(), seconds: input.seconds, loop, bpm: input.bpm,
      vocals: !!input.vocals, ...(typeof input.lyrics === "string" && input.lyrics.trim() ? { lyrics: input.lyrics.trim() } : {}) };
    const item = { kind: "model", jobKind: "music", title: name, loop, spatial: false, bpm: input.bpm,
      want: { path: this.wantedPath(input.path, name, "music", ".ogg").replace(/\.ogg$/i, "") } };
    return this.result(await this.start(job, item, signal), signal);
  }

  // --------------------------------------------------------------- a job

  // Prices a job, asks the user when it is large, starts it, and follows it
  // until it is saved, or until Stop. later: files the server is sent when
  // it asks for them (prepare). Returns its card.
  async start(job, item, signal, later = []) {
    const { cloud } = this.ide;
    const project = this.ide.project();
    item.project = project.id;
    let estimate;
    try {
      ({ estimate } = await cloud.request("POST", "blip/models", { json: { job, estimate: true } }));
    } catch (error) {
      throw this.refused(error);
    }
    item.share = estimate.share;
    if (estimate.share > estimate.left + 1e-6) {
      throw new ModelToolError(`That needs ${percent(estimate.share)} of your energy, and you have ${percent(estimate.left)} left ` +
        "today. Tell the user, and suggest something smaller (fewer animations, no skeleton, a standard style, or a plain " +
        "texture), or trying again tomorrow.");
    }
    // The shared library (docs/SHARED_ASSETS_DESIGN.md): a job that can be
    // shared is shared as the member chose to be asked, or the card asks.
    const library = estimate.library || null;
    item.shareable = !!(library && library.shareable);
    item.rebate = library ? library.rebate : 0;
    let share = item.shareable && library.sharing === "share";
    item.asksSharing = item.shareable && library.sharing === "ask";
    let shown = false;
    if (item.asksSharing || estimate.share >= CONFIRM_SHARE || estimate.share > estimate.left / 2) {
      item.status = "asking";
      this.panel.add(item);
      this.panel.progress(`${item.title}: ${this.stepText(item)}`);
      shown = true;
      const answer = await this.panel.confirm(item, signal);
      if (!answer) {
        item.status = "declined";
        this.panel.update(item);
        throw new ModelToolError(signal && signal.aborted ? "Stopped before it was made." : "The user said not now, so it " +
          "was not made. Ask what they would like instead, or suggest something smaller.");
      }
      if (item.asksSharing) {
        share = !!answer.share;
        if (answer.remember) await this.rememberSharing(share ? "share" : "keep");
      }
    }
    item.shared = share;
    let view;
    const create = () => cloud.request("POST", "blip/models", { json: { job, conversation: this.panel.chat(),
      project: project.id, ...(share ? { share: true } : {}) } });
    try {
      try {
        ({ job: view } = await create());
      } catch (error) {
        // The server needs a file after all, such as a model that did not
        // come from Blip: it is sent, and the job asked for again.
        if (!(error.body && error.body.code === "upload" && later.length)) throw error;
        for (const file of later) await this.send(file);
        ({ job: view } = await create());
      }
    } catch (error) {
      if (item.status === "asking") {
        item.status = "failed";
        item.error = error.message;
        this.panel.update(item);
      }
      throw this.refused(error);
    }
    item.job = view.id;
    item.status = "running";
    this.apply(item, view);
    if (shown) this.panel.update(item);
    else this.panel.add(item);
    this.panel.energyChanged();
    return this.follow(item, signal);
  }

  // The server's refusal, for Claude.
  refused(error) {
    const body = error && error.body || {};
    if (body.code === "allowance") return new ModelToolError(`${error.message} Tell the user, and suggest something smaller or tomorrow.`);
    if (body.code === "global") return new ModelToolError(`${error.message} Making models is resting until tomorrow.`);
    if (body.code === "busy") return new ModelToolError(error.message);
    if (body.code === "plan") return new ModelToolError("The user has no Blitz3D+ membership, which making things needs. " +
      "Tell them they can choose a plan in their account.");
    if (body.code === "signin") return new ModelToolError("The user is not signed in, and making models needs an account.");
    if (error && error.status === 413) return new ModelToolError("The file is too large to send to the IDE's server.");
    if (error && error.status === 400) return new ModelToolError(`The job was not taken: ${error.message}`);
    return new ModelToolError(`The model could not be started: ${error && error.message || error}`);
  }

  // The job's state on its card.
  apply(item, view) {
    item.step = view.step;
    item.waiting = view.waiting;
    item.progress = view.progress;
    item.note = view.note || "";
    item.spent = view.spent ? view.spent.share : 0;
    if (view.estimate) item.share = view.estimate.share;
    item.shareState = view.share || null;
    item.view = view;
  }

  // ------------------------------------------------ the shared library

  // Whether the IDE's server has the shared asset library
  // (docs/SHARED_ASSETS_DESIGN.md, "Blip's tools").
  libraryAvailable() {
    const { cloud } = this.ide;
    return !!(cloud && cloud.config && cloud.config.library);
  }

  // The library's refusal, for Claude.
  libraryRefused(error) {
    const body = error && error.body || {};
    if (body.code === "limit") return new ModelToolError(`${error.message} Tell the user.`);
    if (body.code === "signin") return new ModelToolError("The user is not signed in, and the shared library needs an account.");
    if (error && error.status === 404) return new ModelToolError("There is no such shared asset: use an id find_assets gave.");
    return new ModelToolError(`The shared library could not be reached: ${error && error.message || error}`);
  }

  // What Claude is told of a shared asset: as of a model or picture he made.
  sharedLines(asset) {
    const facts = asset.facts || {};
    // Its rating and downloads, which say what members thought of it.
    const rating = asset.rating && asset.rating.count ?
      `rated ${asset.rating.average} of 5 by ${plural(asset.rating.count, "member")}` : "not rated yet";
    const lines = [`- ${asset.name} (id ${asset.id}): ${asset.kind === "picture" ? "a picture" : "a model"}` +
      `${asset.category ? `, ${asset.category}` : ""}, by ${asset.maker}, ${rating}, downloaded by ` +
      `${plural(asset.downloads || 0, "member")}.`];
    if (asset.description) lines.push(`  ${asset.description}`);
    if (asset.kind === "picture") {
      if (facts.width) lines.push(`  ${facts.width} x ${facts.height} pixels${facts.transparent ? ", with a transparent background" : ""}.`);
    } else {
      const parts = [];
      if (facts.triangles) parts.push(plural(facts.triangles, "triangle"));
      if (facts.size) parts.push(`${number(facts.size[0])} x ${number(facts.size[1])} x ${number(facts.size[2])} m`);
      if (facts.bones) parts.push(`a skeleton of ${plural(facts.bones, "bone")}`);
      if (Array.isArray(facts.clips) && facts.clips.length) {
        parts.push(`clips ${facts.clips.map((clip) => clip.name).join(", ")}`);
      }
      if (parts.length) lines.push(`  ${parts.join("; ")}.`);
    }
    return lines;
  }

  // find_assets: up to six public assets, with their thumbnails for him to
  // see. The panel shows them on a card with Add buttons.
  async findAssets(input) {
    const { cloud } = this.ide;
    const words = typeof input.query === "string" ? input.query.trim().slice(0, 200) : "";
    // What fits his words best; without words, the most popular.
    const query = new URLSearchParams({ q: words, sort: "best" });
    if (input.kind === "model" || input.kind === "picture") query.set("kind", input.kind);
    if (input.animated) query.set("animated", "1");
    let found;
    try {
      found = await cloud.request("GET", `library/assets?${query}`);
    } catch (error) {
      throw this.libraryRefused(error);
    }
    const assets = await Promise.all(found.assets.slice(0, 6).map((asset) =>
      cloud.request("GET", `library/assets/${asset.id}`).then((answer) => answer.asset, () => asset)));
    if (!assets.length) {
      return `Nothing in the shared library matches "${words}". Try other words, or offer to make one with make_model.`;
    }
    if (this.panel && this.panel.assets) {
      this.panel.assets({ kind: "assets", query: words, project: this.ide.project() && this.ide.project().id,
        assets: assets.map(({ id, name, kind, maker, thumbnail, animated }) => ({ id, name, kind, maker, thumbnail, animated })),
        added: {} });
    }
    const content = [{ type: "text", text: `Found ${plural(assets.length, "shared asset")}${found.more ? ", and there are more" : ""}. ` +
      "Each is another member's work, shared for every member to use. The panel shows them with Add buttons; to add " +
      "one yourself, call add_asset with its id." }];
    for (const asset of assets) {
      content.push({ type: "text", text: this.sharedLines(asset).join("\n") });
      if (!asset.thumbnail) continue;
      try {
        const response = await fetch(asset.thumbnail, { credentials: "omit" });
        if (!response.ok) continue;
        const bytes = new Uint8Array(await response.arrayBuffer());
        content.push({ type: "image", source: { type: "base64", media_type: "image/png", data: await pngBase64(bytes, 256) } });
      } catch {}
    }
    return content;
  }

  // add_asset, and the card's Add: saves a shared asset's standard file in
  // the project, at no energy and within the member's limits. Returns what
  // Claude is told, as about a model he made.
  async addAsset(input) {
    const { cloud } = this.ide;
    const project = this.ide.project();
    if (typeof input.id !== "string" || !/^[A-Za-z0-9_-]{8,40}$/.test(input.id)) {
      throw new ModelToolError("Give a shared asset's id, as find_assets gave it.");
    }
    let asset, link;
    try {
      ({ asset } = await cloud.request("GET", `library/assets/${input.id}`));
      const file = asset.files.find((each) => each.variant === "standard");
      if (!file) throw new ModelToolError("That shared asset has no file to add.");
      link = { ...(await cloud.request("GET", `library/assets/${asset.id}/files/${file.hash}?json=1`)), hash: file.hash };
    } catch (error) {
      if (error instanceof ModelToolError) throw error;
      throw this.libraryRefused(error);
    }
    const response = await fetch(link.url, { credentials: "omit" });
    if (!response.ok) throw new ModelToolError(`The shared asset's file could not be fetched (${response.status}).`);
    const blob = await response.blob();
    if (await hashBlob(blob) !== link.hash) throw new ModelToolError("The shared asset's file came damaged. Try again.");
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const picture = asset.kind === "picture";
    if (this.ide.project() !== project) throw new ModelToolError("The project changed while the file came.");
    const path = freePath(project, this.wantedPath(input.path, asset.name, picture ? "pictures" : "models", picture ? ".png" : ".glb"));
    await this.ide.addFile(path, bytes);
    cloud.request("POST", `library/assets/${asset.id}/uses`, { json: {} }).catch(() => {});
    if (this.ide.worldEditor()) await this.ide.putToolFile(path, bytes);
    ++this.made;
    const lines = [`Added ${asset.name}, ${asset.maker}'s shared ${picture ? "picture" : "model"}, as ${path} (${size(bytes.byteLength)}). ` +
      "It cost no energy."];
    if (picture) {
      const facts = asset.facts || {};
      const standard = facts.standard && facts.standard.width ? facts.standard : facts;
      if (standard.width) lines.push(`- ${standard.width} x ${standard.height} pixels${facts.transparent ? ", with a transparent background" : ""}.`);
    } else {
      const info = describeGlb(readGlb(bytes));
      lines.push(`- ${plural(info.triangles, "triangle")}, ${plural(info.materials, "material")}`);
      if (info.bounds) {
        const { min, max } = info.bounds;
        lines.push(`- Size: ${number(max[0] - min[0])} wide (x), ${number(max[1] - min[1])} high (y) and ${number(max[2] - min[2])} ` +
          "deep (z), in metres at real size. It stands as other glTF models do: its feet at y 0, its middle at x 0, z 0, " +
          "and facing the camera (-z) when it is not turned.");
      }
      if (info.bones) lines.push(`- Skeleton: ${plural(info.bones, "bone")}.`);
      if (info.clips.length) {
        lines.push(`- Animation clips, in order (sequence 0 first): ${info.clips.map((clip) => `${clip.name} (${number(clip.seconds)} s)`)
          .join(", ")}. They play in place.`);
      }
      lines.push(info.clips.length || info.bones ? "- Load it with LoadAnimMesh, which keeps its skeleton and clips." :
        "- Load it with LoadMesh, or LoadAnimMesh to keep its parts.");
    }
    if (this.ide.worldEditor()) lines.push("- The World Editor is open: you can add it to the world's assets (world_action import).");
    return lines.join("\n");
  }

  // The member's remembered answer to "Share it?", which the account dialog
  // changes too.
  async rememberSharing(sharing) {
    try {
      await this.ide.cloud.request("PUT", "library/me/sharing", { json: { sharing } });
    } catch (error) {
      console.error("The sharing answer was not remembered:", error);
    }
  }

  // A shared job's way into the library, after the job: the library makes
  // its files, checks it and publishes it, which the card shows as it goes.
  async followShare(item) {
    const { cloud } = this.ide;
    for (let tries = 0; item.shareState && SHARE_PENDING.includes(item.shareState.status) && tries < 120; ++tries) {
      await sleep(SHARE_POLL_MS);
      try {
        const { job: view } = await cloud.request("GET", `blip/models/${item.job}`);
        item.shareState = view.share || null;
        this.panel.update(item);
      } catch (error) {
        if (error.status === 404 || error.status === 401) return;
      }
    }
  }

  // Follows a card's job until its result is saved, or it fails; with a
  // signal, until then or Stop, whichever is first (null for Stop).
  follow(item, signal = null) {
    let promise = this.following.get(item.job);
    if (!promise) {
      promise = this.track(item).finally(() => this.following.delete(item.job));
      this.following.set(item.job, promise);
    }
    if (!signal) return promise;
    return new Promise((resolve, reject) => {
      promise.then(resolve, reject);
      signal.addEventListener("abort", () => resolve(null), { once: true });
    });
  }

  async track(item) {
    const { cloud } = this.ide;
    let failures = 0;
    while (item.status === "running") {
      let view;
      try {
        ({ job: view } = await cloud.request("GET", `blip/models/${item.job}`));
        failures = 0;
      } catch (error) {
        if (error.status === 404 || error.status === 401 && !cloud.user) {
          item.status = "failed";
          item.error = error.status === 404 ? "The server no longer has this job." : "Sign in again to see it.";
          this.panel.update(item);
          return item;
        }
        await sleep(POLL_MS * Math.min(8, 2 ** ++failures));
        continue;
      }
      this.apply(item, view);
      if (view.status === "running") {
        this.panel.update(item);
        if (this.panel.progress) this.panel.progress(`${item.title}: ${this.stepText(item)}`);
        await sleep(POLL_MS);
        continue;
      }
      if (view.status === "failed") {
        item.status = "failed";
        item.error = view.error || `${makerOf(item.jobKind)} could not make it.`;
        item.suggestion = view.suggestion || "";
        this.panel.update(item);
        this.panel.energyChanged();
        return item;
      }
      item.status = "done";
      this.panel.update(item);
      this.panel.energyChanged();
    }
    if (item.status === "done") await this.save(item);
    if (item.shareState) this.followShare(item).catch((error) => console.error(error));
    return item;
  }

  stepText(item) {
    if (item.status === "asking") return "Waiting for your OK";
    if (item.waiting) return "Waiting for a turn";
    return `${STEPS[item.step] || "Making it"}… ${item.progress || 0}%`;
  }

  async fetchFile(job, name) {
    const response = await this.ide.cloud.request("GET", `blip/models/${job}/files/${name}`, { raw: true });
    return new Uint8Array(await response.arrayBuffer());
  }

  // Saves a finished job's result in its project, when that project is open.
  // Returns whether it is saved.
  async save(item) {
    if (item.path) return true;
    if (AUDIO_JOBS.has(item.jobKind)) return this.saveSounds(item);
    const project = this.ide.project();
    if (!project || project.id !== item.project) return false;
    const view = item.view;
    try {
      const files = await Promise.all(view.result.map((name) => this.fetchFile(item.job, name)));
      if (!files.length) throw new Error("Nothing was sent back");
      const preview = view.preview ? await this.fetchFile(item.job, view.preview).catch(() => null) : null;
      let bytes;
      // A texture's other maps, at its colour map's size.
      const maps = {};
      if (item.jobKind === "picture") {
        bytes = await this.fitPicture(item, files[0]);
        item.thumb = await this.thumbnail(bytes, "image/png");
        this.previews.set(item.job, item.texture ? await tiledPreview(bytes) : bytes);
        for (const [kind, name] of Object.entries(view.maps || {})) {
          if (MAP_SUFFIXES[kind]) maps[kind] = await this.fitPicture(item, await this.fetchFile(item.job, name), true);
        }
      } else {
        bytes = await this.finishModel(item, files);
        if (preview) {
          item.thumb = await this.thumbnail(preview, "image/jpeg");
          this.previews.set(item.job, preview);
        }
      }
      // The project may have changed while the files came.
      if (this.ide.project() !== project) return false;
      const path = Object.keys(maps).length ? freeSet(project, item.want.path) : freePath(project, item.want.path);
      await this.ide.addFile(path, bytes);
      item.path = path;
      item.bytes = bytes.byteLength;
      item.maps = {};
      for (const [kind, map] of Object.entries(maps)) {
        item.maps[kind] = mapPath(path, kind);
        await this.ide.addFile(item.maps[kind], map);
      }
      item.status = "saved";
      ++this.made;
      // The World Editor sees it too, for its Import Assets.
      if (this.ide.worldEditor()) {
        await this.ide.putToolFile(path, bytes);
        for (const [kind, map] of Object.entries(maps)) await this.ide.putToolFile(item.maps[kind], map);
      }
    } catch (error) {
      console.error(error);
      item.status = "failed";
      item.error = `It was made, but could not be saved: ${error.message || error}`;
    }
    this.panel.update(item);
    return item.status === "saved";
  }

  // A sound's files, kept as the server made them: one, or its variations
  // numbered on from the first free number (footstep-1.wav, and then
  // footstep-5.wav for four more).
  async saveSounds(item) {
    const project = this.ide.project();
    if (!project || project.id !== item.project) return false;
    const view = item.view;
    try {
      const files = await Promise.all(view.result.map((name) => this.fetchFile(item.job, name)));
      if (!files.length) throw new Error("Nothing was sent back");
      if (this.ide.project() !== project) return false;
      const exts = view.result.map((name) => name.replace(/^.*(\.[a-z0-9]+)$/i, "$1"));
      const paths = files.length === 1 ? [freePath(project, `${item.want.path}${exts[0]}`)] : freeNumbers(project, item.want.path, exts);
      for (const [index, path] of paths.entries()) await this.ide.addFile(path, files[index]);
      item.path = paths[0];
      item.paths = paths;
      item.sounds = (view.sounds || []).map(({ name, ...facts }) => facts);
      item.bytes = files.reduce((sum, bytes) => sum + bytes.byteLength, 0);
      item.status = "saved";
      ++this.made;
      if (this.ide.worldEditor()) for (const [index, path] of paths.entries()) await this.ide.putToolFile(path, files[index]);
    } catch (error) {
      console.error(error);
      item.status = "failed";
      item.error = `It was made, but could not be saved: ${error.message || error}`;
    }
    this.panel.update(item);
    return item.status === "saved";
  }

  // A model as it is kept: its animations, sent as one file or several,
  // joined, with the names asked for; and where it came from, noted in it.
  async finishModel(item, files) {
    const view = item.view;
    if (!files.every(isGlb)) throw new Error("Tripo sent a model that is not a GLB file");
    let glb = readGlb(files[0]);
    if (files.length > 1) glb = joinClips(glb, files.slice(1).map(readGlb));
    let moves = view.animations.map((animation) => animation.move);
    const named = nameClips(glb, view.animations.map((animation) => animation.clip));
    // More animations for a skeleton Blip made join the model's own clips.
    let base = null;
    if (item.change === "animate" && item.from && view.tasks.rig && this.ide.project().has(item.from)) {
      try {
        base = readGlb(new Uint8Array(await this.ide.project().blob(item.from).arrayBuffer()));
        const origin = describeGlb(base).origin;
        if (!origin || origin.rig !== view.tasks.rig) base = null;
        else moves = [...(origin.animations || []), ...moves];
      } catch {
        base = null;
      }
    }
    if (base && named) glb = joinClips(base, [glb]);
    // A change without a model of its own, such as more animations, keeps
    // the model it started from.
    const origin = item.origin || {};
    noteOrigin(glb, { job: item.job, model: view.tasks.model || origin.model || null, rig: view.tasks.rig || null,
      skeleton: view.skeleton || null, animations: [...new Set(moves)] }, GENERATOR);
    // It stands as other glTF models do, with textures sized for the web.
    standGlb(glb);
    await shrinkTextures(glb, item.textureSize || 1024, resizePicture);
    item.info = describeGlb(glb);
    return writeGlb(glb);
  }

  // A picture at the size asked for: as made, or smaller (a sprite 256
  // pixels at most, any other picture 1024), keeping its shape when only
  // one side is given. A texture's other map (map) takes its colour map's
  // size, and its pixels as they are: they are data, not colours.
  async fitPicture(item, bytes, map = false) {
    const image = await bitmap(bytes);
    const ratio = image.width / image.height;
    image.close();
    let { width, height } = map ? item.pictureSize : item.want;
    if (!width && !height) {
      const longest = item.transparent ? 256 : 1024;
      width = ratio >= 1 ? longest : Math.round(longest * ratio);
      height = ratio >= 1 ? Math.round(longest / ratio) : longest;
    } else if (!height) {
      height = Math.max(1, Math.round(width / ratio));
    } else if (!width) {
      width = Math.max(1, Math.round(height * ratio));
    }
    const canvas = await scaled(bytes, width, height);
    if (!map) item.pictureSize = { width, height };
    return new Uint8Array(await (await canvas.convertToBlob({ type: "image/png" })).arrayBuffer());
  }

  // A small picture for the card.
  async thumbnail(bytes, type) {
    try {
      const image = await bitmap(bytes);
      const width = Math.min(THUMB_WIDTH, image.width), height = Math.max(1, Math.round(image.height * width / image.width));
      image.close();
      const canvas = await scaled(bytes, width, height);
      return dataUrl(await canvas.convertToBlob(type === "image/png" ? { type } : { type: "image/jpeg", quality: 0.85 }));
    } catch {
      return null;
    }
  }

  // ------------------------------------------------------------- results

  // What a tool returns: what was made, for Claude, with its picture; or,
  // after Stop, that it carries on.
  async result(item, signal) {
    if (!item || item.status === "running" || item.status === "done" && !item.path) {
      if (signal && signal.aborted) {
        return "Stopped waiting. It is still being made, and it will be saved in the project when it is ready: its card in " +
          "your panel shows how it is going.";
      }
      return "It was made, and will be saved in the project when it is open again.";
    }
    if (item.status === "failed") {
      throw new ModelToolError((item.error || `${makerOf(item.jobKind)} could not make it.`) + (item.suggestion ?
        ` ElevenLabs suggests describing it as: "${item.suggestion}". Try again with words like those; a refusal cost no energy.` : ""));
    }
    const lines = [];
    const view = item.view;
    if (AUDIO_JOBS.has(item.jobKind)) {
      lines.push(...this.soundLines(item));
    } else if (item.jobKind === "picture" && item.texture) {
      const { width, height } = item.pictureSize || {};
      const maps = item.maps || {};
      lines.push(`Made the texture ${item.title}, ${width} x ${height} pixels, drawn to repeat without seams. Its colour map is ` +
        `${item.path}${Object.keys(maps).length ? ", with its other maps beside it:" : "."}`);
      if (maps.normal) lines.push(`- ${maps.normal}: its normal map, worked out from its height map (OpenGL, green up).`);
      if (maps.height) lines.push(`- ${maps.height}: its height map, white high.`);
      if (maps.metallicRoughness) {
        lines.push(`- ${maps.metallicRoughness}: its roughness, packed as the Standard material's metallicRoughness map ` +
          `(green rough, blue metal: ${item.metal ? "all metal" : "none, as it is not metal"}).`);
      }
      lines.push(this.ide.worldEditor() ? "- To put it on objects in the World Editor: world_action paint with texture " +
        `"${item.path}" and tileSize, the metres one copy covers, such as 1 for cobbles or 2 for planks; paint finds the maps ` +
        "and imports them." : "- In a program: LoadTexture its colour map, EntityTexture it, and ScaleTexture it to repeat.");
      lines.push("- The picture below is four copies side by side: check that the joins can't be seen.");
      lines.push(drawnLine(view));
    } else if (item.jobKind === "picture") {
      const { width, height } = item.pictureSize || {};
      lines.push(`Made ${item.title} and saved it as ${item.path} (${width} x ${height} pixels, ${size(item.bytes)}` +
        `${item.transparent ? ", with a transparent background" : ""}).`);
      lines.push(drawnLine(view));
    } else {
      const info = item.info;
      lines.push(item.jobKind === "change" ? `Changed ${item.from} and saved the result as ${item.path} (${size(item.bytes)}); ` +
        `${item.from} is as it was.` : `Made ${item.title} and saved it as ${item.path} (${size(item.bytes)}).`);
      const pictures = info.pictures.filter((picture) => picture.width);
      lines.push(`- ${plural(info.triangles, "triangle")}, ${plural(info.materials, "material")}` +
        (pictures.length ? `, ${plural(pictures.length, "texture")} (${[...new Set(pictures.map((picture) => `${picture.width} x ${picture.height}`))].join(", ")})` :
          ", no textures"));
      if (info.bounds) {
        const { min, max } = info.bounds;
        lines.push(`- Size: ${number(max[0] - min[0])} wide (x), ${number(max[1] - min[1])} high (y) and ${number(max[2] - min[2])} ` +
          "deep (z), in metres at real size. It stands as other glTF models do: its feet at y 0, its middle at x 0, z 0, " +
          "and facing the camera (-z) when it is not turned.");
      }
      if (info.bones) {
        const examples = info.boneNames.filter((bone) => /hand|head|hip|spine|foot/i.test(bone)).slice(0, 5);
        lines.push(`- Skeleton: ${SKELETONS[view.skeleton] || "a"}, ${plural(info.bones, "bone")}` +
          `${examples.length ? `, such as ${examples.join(", ")}` : ""}.`);
      }
      if (info.clips.length) {
        lines.push(`- Animation clips, in order (sequence 0 first): ${info.clips.map((clip) => `${clip.name} (${number(clip.seconds)} s)`)
          .join(", ")}. They play in place.`);
      } else if (view.animations.length) {
        lines.push("- It came without animation clips.");
      }
      lines.push(info.clips.length || info.bones ? "- Load it with LoadAnimMesh, which keeps its skeleton and clips." :
        "- Load it with LoadMesh, or LoadAnimMesh to keep its parts.");
    }
    if (item.kept && item.kept.length) {
      lines.push(`- The user's ${item.kept.length === 1 ? "picture it was made from is" : "pictures it was made from are"} ` +
        `kept beside it: ${item.kept.join(", ")}.`);
    }
    if (item.note) lines.push(`- Note: ${item.note}`);
    lines.push(`- It used ${percent(item.spent || 0)} of today's energy.`);
    if (this.ide.worldEditor() && !item.texture && !AUDIO_JOBS.has(item.jobKind)) {
      lines.push("- The World Editor is open: its card offers to add it to the world's assets, or you can (world_action import).");
    }
    if (AUDIO_JOBS.has(item.jobKind)) {
      lines.push("- You can't hear it: ask the user to listen with the play button on its card, and to say if it should change. " +
        "Another one costs energy again.");
    }
    const content = [{ type: "text", text: lines.join("\n") }];
    const picture = this.previews.get(item.job);
    if (picture) {
      try {
        content.push({ type: "image", source: { type: "base64", media_type: "image/png", data: await pngBase64(picture, 640) } });
      } catch (error) {
        console.error(error);
      }
    }
    return content;
  }

  // What Claude is told of a sound or music he made: where it is, what the
  // server found of it, and how to play it in code and in the world.
  soundLines(item) {
    const sounds = item.sounds || [], first = sounds[0] || {}, paths = item.paths || [item.path];
    const music = item.jobKind === "music";
    const seconds = (value) => `${number(value || 0)} s`;
    const lines = [];
    const what = `${first.channels === 1 ? "mono" : "stereo"}${item.loop ? ", looping" : ""}`;
    if (paths.length > 1) {
      lines.push(`Made ${paths.length} variations of ${item.title} and saved them as ${paths.join(", ")} (${sounds.map((sound) =>
        seconds(sound.seconds)).join(", ")}; ${what}; ${size(item.bytes)} in all).`);
    } else {
      lines.push(`Made ${item.title} and saved it as ${item.path} (${seconds(first.seconds)}, ${what}, ${size(item.bytes)}).`);
    }
    if (music && first.bars) lines.push(`- It is ${first.bars} bars at ${item.bpm} BPM, cut at whole bars.`);
    if (item.loop) {
      const joins = sounds.map((sound) => sound.join ?? 1);
      const worst = Math.min(...joins);
      lines.push(worst >= 0.85 ? "- Its end leads into its start, so it repeats without a join." :
        worst >= 0.7 ? "- Its end leads into its start; the join is probably not heard, but ask the user to listen for it." :
          "- The join where it repeats may be heard: say so, and offer another try.");
    } else if (!music) {
      const attack = Math.max(...sounds.map((sound) => sound.attack || 0)), release = Math.max(...sounds.map((sound) => sound.release || 0));
      lines.push(`- It ${attack < 0.05 ? "starts at once" : `builds up over ${seconds(attack)}`}` +
        `${release > 0.2 ? ` and dies away over ${seconds(release)}` : ""}; the silence before it was trimmed off.`);
    }
    const path = item.path;
    if (music) {
      lines.push(item.loop ? `- In a program: music = LoadSound("${path}") : LoopSound music : channel = PlaySound(music), ` +
        "which repeats without a gap; StopChannel channel stops it." : `- In a program: channel = PlayMusic("${path}") plays it ` +
        "once; ChannelPlaying(channel) says when it has ended.");
    } else if (item.spatial) {
      lines.push(`- In a program: sound = Load3DSound("${paths.length > 1 ? paths[0] : path}")${item.loop ? " : LoopSound sound" : ""}, ` +
        "then EmitSound sound, entity plays it from an entity (CreateListener on the camera first, so that it is heard " +
        "from there).");
    } else {
      lines.push(`- In a program: sound = LoadSound("${paths.length > 1 ? paths[0] : path}")${item.loop ? " : LoopSound sound" : ""}, ` +
        "then PlaySound sound.");
    }
    if (paths.length > 1) lines.push(`- Load each variation, and play one picked with Rand(1, ${paths.length}) each time, so that it doesn't repeat.`);
    if (this.ide.worldEditor()) {
      lines.push(music ? `- The World Editor is open: world_action soundscape with music "${path}" makes it the world's music ` +
        "(or a zone's, with object). Its card offers it too." : item.loop ? "- The World Editor is open: world_action " +
        `soundscape with beds [{ "path": "${path}" }] plays it everywhere, quietly behind everything (or in a zone); ` +
        "world_action sound plays it from an object." : "- The World Editor is open: a one-shot plays on an event: as a " +
        "collectible's sound, a trigger zone's playSound, or now and then in a soundscape's oneShots (world_action " +
        "soundscape). Import it first (world_action import) where an asset's id is needed.");
    }
    return lines;
  }
}

// Paths for a sound's variations, stem-n with each one's extension, numbered
// on from the first number from which they are all free.
export function freeNumbers(project, stem, exts) {
  const taken = new Set(project.paths().map((path) => path.toLowerCase()));
  for (let first = 1; ; ++first) {
    const paths = exts.map((ext, index) => `${stem}-${first + index}${ext}`);
    if (paths.every((path) => !taken.has(path.toLowerCase()))) return paths;
  }
}

// A path for a texture's colour map that its maps' paths are free beside
// too: cobbles.png, then cobbles-2.png.
export function freeSet(project, wanted) {
  const taken = new Set(project.paths().map((path) => path.toLowerCase()));
  const free = (path) => !taken.has(path.toLowerCase()) &&
    Object.keys(MAP_SUFFIXES).every((kind) => !taken.has(mapPath(path, kind).toLowerCase()));
  if (free(wanted)) return wanted;
  const stem = wanted.replace(/\.png$/i, "");
  for (let n = 2; ; ++n) if (free(`${stem}-${n}.png`)) return `${stem}-${n}.png`;
}

// A texture shown as four copies side by side, as Claude checks its joins.
async function tiledPreview(bytes) {
  const image = await bitmap(bytes);
  const canvas = new OffscreenCanvas(image.width * 2, image.height * 2);
  const context = canvas.getContext("2d");
  for (let y = 0; y < 2; ++y) for (let x = 0; x < 2; ++x) context.drawImage(image, x * image.width, y * image.height);
  image.close();
  return new Uint8Array(await (await canvas.convertToBlob({ type: "image/png" })).arrayBuffer());
}

// How a picture was drawn, which the user's effort setting chose, for Claude.
function drawnLine(view) {
  const image = view && view.image;
  if (!image) return "- Drawn as the user's effort setting chose.";
  const model = image.model.replace(/^gpt-image-([\d.]+)-(\w+)$/, (all, version, name) =>
    `GPT Image ${version} ${name[0].toUpperCase()}${name.slice(1)}`);
  return `- Drawn by ${model} at ${image.quality} quality, as the user's ${image.effort} effort setting chose: a higher ` +
    "setting draws more detail and uses more energy.";
}

// A card's pictures it is made from: the first's thumbnail, and where the
// user's pictures were kept.
function sourcesOf(sources) {
  const kept = sources.map((source) => source.kept).filter(Boolean);
  return { ...(sources.length && sources[0].thumb ? { source: sources[0].thumb } : {}), ...(kept.length ? { kept } : {}) };
}

const base64 = (bytes) => {
  let binary = "";
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  return btoa(binary);
};

// A texture made size pixels across at most, as JPEG, or PNG when it was.
async function resizePicture(bytes, type, size) {
  const image = await bitmap(bytes);
  const scale = size / Math.max(image.width, image.height);
  const width = Math.max(1, Math.round(image.width * scale)), height = Math.max(1, Math.round(image.height * scale));
  image.close();
  const canvas = await scaled(bytes, width, height);
  const png = type === "image/png";
  const blob = await canvas.convertToBlob(png ? { type } : { type: "image/jpeg", quality: 0.9 });
  return { bytes: new Uint8Array(await blob.arrayBuffer()), type: png ? "image/png" : "image/jpeg" };
}

// A picture of any kind, at most width wide, as standard base64 PNG, which
// the API takes.
async function pngBase64(bytes, width) {
  const image = await bitmap(bytes);
  const scale = Math.min(1, width / image.width);
  const canvas = await scaled(bytes, Math.max(1, Math.round(image.width * scale)), Math.max(1, Math.round(image.height * scale)));
  image.close();
  return base64(new Uint8Array(await (await canvas.convertToBlob({ type: "image/png" })).arrayBuffer()));
}
